import {
  adjustToTimeZone,
  cargoSubtype,
  CompanyEntity,
  ContainerEntity,
  FeetInches,
  firstOrUndefined,
  FreightTypeEntity,
  getObjectId,
  getObjectName,
  isStringUndefinedOrBlank,
  Message,
  PersonEntity,
  Quantity,
  RangeQuantity,
  Seal,
  ServiceTypeEntity,
  SiteEntity,
  toTransportCategory,
  toTransportRejectionType,
  toValveType,
  TrailerEntity,
  transportCategories,
  transportCategory,
  TransportCategory,
  TransportGauge,
  TransportPayload,
  TransportRejectionType,
  TransportType,
  transportType,
  unitOfMeasureTypes,
  useGetPreferredFreightType,
  VehicleEntity,
  WellEntity
} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  NumberInput,
  SegmentedControl,
  Table,
  Textarea,
  TextInput
} from "@mantine/core";
import {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {ServiceTypeSelect} from "../../select/entity/ServiceTypeSelect/ServiceTypeSelect";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";
import {WellSelect} from "../../select/entity/WellSelect/WellSelect";
import {ContainerSelect} from "../../select/entity/ContainerSelect/ContainerSelect";
import {getTransportCategoryColorRefByType} from "../../../util/transport-category-ui-util";
import {DriverSelect} from "../../select/entity/PersonSelect/DriverSelect";
import {VehicleSelect} from "../../select/entity/VehicleSelect/VehicleSelect";
import {TrailerSelect} from "../../select/entity/TrailerSelect/TrailerSelect";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {
  TransportRejectionTypeSelect
} from "../../select/entity/TransportRejectionTypeSelect/TransportRejectionTypeSelect";
import {useTicketForm} from "../TicketForm/TicketFormProvider/TicketFormProvider";
import {isEqual} from "lodash";
import {
  IconCalendar,
  IconDots,
  IconEdit,
  IconGauge,
  IconLocation,
  IconMessage,
  IconRulerMeasure,
  IconStack,
  IconTag,
  IconTrash,
  IconUser
} from "@tabler/icons-react";
import {useTransportForm} from "./useTransportForm";
import {useDisclosure} from "@mantine/hooks";
import {TransportGaugeFormModal, TransportGaugeFormValues,} from "../TransportGaugeForm/TransportGaugeForm";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {Card} from "../../card/Card/Card";
import {ValveTypeSelect} from "../../select/entity/ValveTypeSelect/ValveTypeSelect";

export interface TransportFormProps {
  type: TransportType
  options?: TransportFormOptions;
  defaultValues?: TransportFormDefaultValues;
  submitting?: boolean
  onSubmit?: (values: TransportFormValues) => void
}

export interface TransportFormOptions {
  regionId?: string
  category?: TransportCategory
  categoryDisabled?: boolean
  personSelectionDisabled?: boolean;
  siteSelectionDisabled?: boolean;
  wellSelectionDisabled?: boolean;
  containerSelectionDisabled?: boolean;
  hasGeofenceEvent?: boolean
}

export interface TransportFormValues {
  date: Date;
  inDate?: Date;
  outDate?: Date;
  disableDemurrage?: boolean;
  skipGeofenceEvent?: boolean;
  person?: PersonEntity | string;
  vehicle?: VehicleEntity | string;
  trailer?: TrailerEntity | string;
  odometer?: Quantity
  rejectionType?: TransportRejectionType
  rejectionComment?: string
  quantity: Quantity;
  netQuantity?: number
  observedGravity?: number
  correctedGravity?: number
  bswPercentage?: number
  observedTemperature?: Quantity
  topTemperature?: number
  bottomTemperature?: number
  freightType?: FreightTypeEntity | string;
  serviceType?: ServiceTypeEntity | string;
  siteCompany?: CompanyEntity | string
  site?: SiteEntity | string;
  well?: WellEntity | string;
  container?: ContainerEntity | string;
  meterReading?: RangeQuantity
  seal?: Seal
  seal2?: Seal
  seal3?: Seal
  ticketNumber?: string
  workOrderNumber?: string
  internalWorkOrderNumber?: string
  regulatoryNumber?: string
  // TODO: seals dates,
  gauges?: TransportGaugeFormValues[],
  comments?: string;
  category: TransportCategory
}

export interface TransportFormDefaultValues extends Partial<Omit<TransportFormValues, 'odometer' | 'quantity' | 'observedTemperature' | 'meterReading' | 'seal' | 'seal2' | 'seal3'>> {
  odometer?: Partial<Quantity>,
  quantity?: Partial<Quantity>
  observedTemperature?: Partial<Quantity>,
  meterReading?: Partial<RangeQuantity>,
  seal?: Partial<Seal>,
  seal2?: Partial<Seal>,
  seal3?: Partial<Seal>,
}

export interface TransportFormRawValues {
  date?: Date;
  inDate?: Date;
  outDate?: Date;
  disableDemurrage?: boolean
  skipGeofenceEvent?: boolean
  person?: string;
  vehicle?: string;
  trailer?: string;
  odometer?: number
  odometerUom?: string
  rejectionType?: string
  rejectionComment?: string
  quantity?: number;
  quantityUom?: string
  netQuantity?: number
  observedGravity?: number
  correctedGravity?: number
  bswPercentage?: number
  observedTemperature?: number
  temperatureUom?: string
  topTemperature?: number
  bottomTemperature?: number
  freightType?: string;
  serviceType?: string;
  siteCompany?: string
  site?: string;
  well?: string;
  container?: string;
  meterStart?: number
  meterEnd?: number
  meterUom?: string
  sealOff?: string
  sealOn?: string
  sealValveType?: string
  seal2Off?: string
  seal2On?: string
  seal2ValveType?: string
  seal3Off?: string
  seal3On?: string
  seal3ValveType?: string
  ticketNumber?: string
  workOrderNumber?: string
  internalWorkOrderNumber?: string
  regulatoryNumber?: string
  // TODO: seals,
  gauges: TransportGaugeFormValues[]
  comments?: string;
  category?: string
}

export const getTransportPatch = (formValues: TransportFormValues, regionId: string | undefined): TransportPayload => {

  let messages: Partial<Message>[] | undefined = undefined
  let gauges: Partial<TransportGauge>[] | undefined = undefined

  if (formValues.comments && !isStringUndefinedOrBlank(formValues.comments)) {
    const message: Partial<Message> = {
      message: formValues.comments,
      messageType: "COMMENT",
      action: "CREATE"
    }

    messages = [];
    messages.push(message);
  }

  if (formValues.gauges) {
    for (const formGauge of formValues.gauges) {
      if (!gauges) {
        gauges = []
      }

      gauges?.push({
        regionId: regionId,
        containerId: getObjectId(formGauge.container),
        type: formGauge.type.code,
        topValue: formGauge.topValue,
        bottomValue: formGauge.bottomValue
      })
    }
  }

  return {
    createDttm: adjustToTimeZone(formValues.date).getTime(),
    inDttm: adjustToTimeZone(formValues.inDate)?.getTime(),
    outDttm: adjustToTimeZone(formValues.outDate)?.getTime(),
    disableDemurrage: formValues.disableDemurrage,
    skipGeofenceEvent: formValues.skipGeofenceEvent,
    personId: getObjectId(formValues.person),
    trailerId: getObjectId(formValues.trailer),
    vehicleId: getObjectId(formValues.vehicle),
    odometer: formValues.odometer?.number,
    odometerUom: formValues.odometer?.units,
    quantityDecimal: formValues.quantity.number,
    quantityUnitOfMeasure: formValues.quantity.units,
    rejectionType: formValues.rejectionType?.code,
    rejectionComment: formValues.rejectionComment,
    netQuantity: formValues.netQuantity,
    observedGravity: formValues.observedGravity,
    gravityAt60: formValues.correctedGravity,
    bswPercentage: formValues.bswPercentage,
    observedTemperature: formValues.observedTemperature?.number,
    temperatureUom: formValues.observedTemperature?.units,
    topTemperature: formValues.topTemperature,
    bottomTemperature: formValues.bottomTemperature,
    waterTypeId: getObjectId(formValues.freightType),
    serviceTypeId: getObjectId(formValues.serviceType),
    siteId: getObjectId(formValues.site),
    wellId: getObjectId(formValues.well),
    containerId: getObjectId(formValues.container),
    ticketNumber: formValues.ticketNumber,
    workOrderNumber: formValues.workOrderNumber,
    internalWorkOrderNumber: formValues.internalWorkOrderNumber,
    regulatoryNumber: formValues.regulatoryNumber,
    meterReading: formValues.meterReading?.start,
    meterReadingEnd: formValues.meterReading?.end,
    meterReadingUom: formValues.meterReading?.units,
    sealOff: formValues.seal?.off,
    sealOn: formValues.seal?.on,
    sealValveType: formValues?.seal?.valveType?.code,
    seal2Off: formValues.seal2?.off,
    seal2On: formValues.seal2?.on,
    seal2ValveType: formValues?.seal2?.valveType?.code,
    seal3Off: formValues.seal3?.off,
    seal3On: formValues.seal3?.on,
    seal3ValveType: formValues?.seal3?.valveType?.code,
    sealOffDttm: formValues.seal?.offDttm,
    sealOnDttm: formValues.seal?.onDttm,
    seal2OffDttm: formValues.seal2?.offDttm,
    seal2OnDttm: formValues.seal2?.onDttm,
    seal3OffDttm: formValues.seal3?.offDttm,
    seal3OnDttm: formValues.seal3?.onDttm,
    gauges: gauges,
    regionId: regionId,
    messages: messages,
    category: formValues.category.code,
  }
}

export const getInitialValues = (defaultValues: TransportFormDefaultValues | undefined): TransportFormRawValues => {
  return {
    date: defaultValues?.date ?? new Date(),
    inDate: defaultValues?.inDate,
    outDate: defaultValues?.outDate,
    disableDemurrage: defaultValues?.disableDemurrage,
    skipGeofenceEvent: defaultValues?.skipGeofenceEvent,
    person: getObjectId(defaultValues?.person),
    vehicle: getObjectId(defaultValues?.vehicle),
    trailer: getObjectId(defaultValues?.trailer),
    odometer: defaultValues?.odometer?.number,
    odometerUom: defaultValues?.odometer?.units,
    rejectionType: defaultValues?.rejectionType?.code,
    rejectionComment: defaultValues?.rejectionComment,
    quantity: defaultValues?.quantity?.number,
    quantityUom: defaultValues?.quantity?.units,
    netQuantity: defaultValues?.netQuantity,
    observedGravity: defaultValues?.observedGravity,
    correctedGravity: defaultValues?.correctedGravity,
    bswPercentage: defaultValues?.bswPercentage,
    observedTemperature: defaultValues?.observedTemperature?.number,
    temperatureUom: defaultValues?.observedTemperature?.units,
    topTemperature: defaultValues?.topTemperature,
    bottomTemperature: defaultValues?.bottomTemperature,
    freightType: getObjectId(defaultValues?.freightType),
    serviceType: getObjectId(defaultValues?.serviceType),
    siteCompany: getObjectId(defaultValues?.siteCompany),
    site: getObjectId(defaultValues?.site),
    well: getObjectId(defaultValues?.well),
    container: getObjectId(defaultValues?.container),
    meterStart: defaultValues?.meterReading?.start,
    meterEnd: defaultValues?.meterReading?.end,
    meterUom: defaultValues?.meterReading?.units,
    sealOff: defaultValues?.seal?.off,
    sealOn: defaultValues?.seal?.on,
    sealValveType: defaultValues?.seal?.valveType?.code,
    seal2Off: defaultValues?.seal2?.off,
    seal2On: defaultValues?.seal2?.on,
    seal2ValveType: defaultValues?.seal2?.valveType?.code,
    seal3Off: defaultValues?.seal3?.off,
    seal3On: defaultValues?.seal3?.on,
    seal3ValveType: defaultValues?.seal3?.valveType?.code,
    ticketNumber: defaultValues?.ticketNumber,
    workOrderNumber: defaultValues?.workOrderNumber,
    internalWorkOrderNumber: defaultValues?.internalWorkOrderNumber,
    regulatoryNumber: defaultValues?.regulatoryNumber,
    // TODO: seal dates,
    // TODO: gauges,
    gauges: defaultValues?.gauges ?? [],
    comments: defaultValues?.comments,
  }
}

export const toFormValues = (values: TransportFormRawValues): TransportFormValues => {
  return {
    date: values.date!,
    inDate: values.inDate,
    outDate: values.outDate,
    disableDemurrage: values.disableDemurrage,
    skipGeofenceEvent: values.skipGeofenceEvent,
    person: values.person!,
    vehicle: values.vehicle,
    trailer: values.trailer,
    odometer: {
      number: values.odometer!,
      units: values.odometerUom!
    },
    rejectionType: values.rejectionType ? toTransportRejectionType(values.rejectionType) : undefined,
    rejectionComment: values.rejectionComment,
    quantity: {
      number: values.quantity!,
      units: values.quantityUom!
    },
    netQuantity: values.netQuantity,
    observedGravity: values.observedGravity,
    correctedGravity: values.correctedGravity,
    bswPercentage: values.bswPercentage,
    observedTemperature: {
      number: values.observedTemperature!,
      units: values.temperatureUom!
    },
    topTemperature: values.topTemperature,
    bottomTemperature: values.bottomTemperature,
    freightType: values.freightType,
    serviceType: values.serviceType,
    site: values.site,
    well: values.well,
    container: values.container,
    meterReading: {
      start: values.meterStart,
      end: values.meterEnd,
      units: values.meterUom,
    },
    seal: {
      off: values.sealOff,
      on: values.sealOn,
      valveType: values.sealValveType ? toValveType(values.sealValveType) : undefined,
    },
    seal2: {
      off: values.seal2Off,
      on: values.seal2On,
      valveType: values.seal2ValveType ? toValveType(values.seal2ValveType) : undefined,
    },
    seal3: {
      off: values.seal3Off,
      on: values.seal3On,
      valveType: values.seal3ValveType ? toValveType(values.seal3ValveType) : undefined,
    },
    ticketNumber: values.ticketNumber,
    workOrderNumber: values.workOrderNumber,
    internalWorkOrderNumber: values.internalWorkOrderNumber,
    regulatoryNumber: values.regulatoryNumber,
    gauges: values.gauges,
    comments: values.comments,
    category: toTransportCategory(values.category!),
  }
}

export type TransportFormType = UseFormReturnType<TransportFormRawValues, (values: TransportFormRawValues) => TransportFormRawValues>

export const TransportForm = forwardRef<HTMLFormElement, TransportFormProps>((props, ref) => {
  const {
    type,
    options,
    defaultValues,
    submitting = false,
    onSubmit,
  } = props;

  const {t} = useTranslation(["common"]);

  const [category, setCategory] = useState<TransportCategory>(defaultValues?.category ?? options?.category ?? transportCategory.water)
  const [selectedGauge, setSelectedGauge] = useState<TransportGaugeFormValues | undefined>()
  const previousOpposingValues = useRef<TransportFormRawValues | undefined>(undefined);

  const [gaugeOpened, gaugeHandler] = useDisclosure(false);

  const {
    form: defaultForm,
    previousValues
  } = useTransportForm({
    type: type,
    defaultValues: defaultValues,
  })

  const {
    pickupForm,
    pickupValues,
    setPickupValues,
    deliveryForm,
    deliveryValues,
    setDeliveryValues
  } = useTicketForm()

  let form: TransportFormType = defaultForm
  if (pickupForm && type === transportType.pickup) {
    form = pickupForm
  } else if (deliveryForm) {
    form = deliveryForm
  }

  useEffect(() => {
    form.setFieldValue('category', category.code)
  }, [])

  const {freightTypeId: preferredFreightTypeId} = useGetPreferredFreightType({
    serviceTypeId: getObjectId(form.values.serviceType),
    regionId: options?.regionId,
  });

  useEffect(() => {
    if (preferredFreightTypeId && !form.values.freightType) {
      form.setFieldValue("freightType", preferredFreightTypeId);
    }
  }, [form, preferredFreightTypeId]);

  const opposingValues = useMemo(() => {
    return type === transportType.pickup ? deliveryValues : pickupValues
  }, [type, pickupValues, deliveryValues])

  const valueSetter = useMemo(() => {
    return type === transportType.pickup ? setPickupValues : setDeliveryValues
  }, [type, setPickupValues, setDeliveryValues])

  useEffect(() => {
    if (type === transportType.delivery) {
      if (!isEqual(opposingValues?.person, previousOpposingValues.current?.person)) {
        form.setFieldValue("person", opposingValues?.person)
      }
      if (!isEqual(opposingValues?.vehicle, previousOpposingValues.current?.vehicle)) {
        form.setFieldValue("vehicle", opposingValues?.vehicle)
      }
      if (!isEqual(opposingValues?.trailer, previousOpposingValues.current?.trailer)) {
        form.setFieldValue("trailer", opposingValues?.trailer)
      }
      if (!isEqual(opposingValues?.freightType, previousOpposingValues.current?.freightType)) {
        form.setFieldValue("freightType", opposingValues?.freightType)
      }
      if (!isEqual(opposingValues?.serviceType, previousOpposingValues.current?.serviceType)) {
        form.setFieldValue("serviceType", opposingValues?.serviceType)
      }
      if (!isEqual(opposingValues?.quantity, previousOpposingValues.current?.quantity)) {
        form.setFieldValue("quantity", opposingValues?.quantity)
      }
      if (!isEqual(opposingValues?.quantityUom, previousOpposingValues.current?.quantityUom)) {
        form.setFieldValue("quantityUom", opposingValues?.quantityUom)
      }
      if (!isEqual(opposingValues?.odometerUom, previousOpposingValues.current?.odometerUom)) {
        form.setFieldValue("odometerUom", opposingValues?.odometerUom)
      }
      if (!isEqual(opposingValues?.temperatureUom, previousOpposingValues.current?.temperatureUom)) {
        form.setFieldValue("temperatureUom", opposingValues?.temperatureUom)
      }
    }

    if (!isEqual(opposingValues?.category, previousOpposingValues.current?.category)) {
      if (opposingValues?.category) {
        setCategory(toTransportCategory(opposingValues.category))
      }
    }

    if (!isEqual(opposingValues?.rejectionType, previousOpposingValues.current?.rejectionType)) {
      form.setFieldValue("rejectionType", opposingValues?.rejectionType)
    }

    previousOpposingValues.current = opposingValues
  }, [type, form, opposingValues]);

  useEffect(() => {
    if (valueSetter) {
      const values = form.values
      if (!isEqual(values, previousValues.current)) {
        valueSetter({...form.values})
      }
      previousValues.current = values
    }
  }, [form, form.values, valueSetter]);

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit(toFormValues(values));
    }
  }, [form, onSubmit]);

  const handleSiteCompanyChange = useCallback((company: CompanyEntity | undefined): void => {
    form.setFieldValue("site", undefined);
    form.setFieldValue("container", undefined);
    form.setFieldValue("well", undefined);
  }, [form]);

  const handleSiteChange = useCallback((site: SiteEntity | undefined): void => {
    if (site) {
      const selectedPickupSiteCompanyId = form.getTransformedValues().siteCompany;

      if (site.companyId) {
        if (selectedPickupSiteCompanyId !== site.companyId) {
          form.setFieldValue("siteCompany", site.companyId);
        }
      }
    }

    form.setFieldValue("container", undefined);
    form.setFieldValue("well", undefined);
  }, [form]);

  const handleWellChange = useCallback((well: WellEntity | undefined): void => {
    if (well) {
      const selectedPickupSiteCompanyId = form.getTransformedValues().siteCompany;

      if (well.siteCompanyId) {
        if (selectedPickupSiteCompanyId !== well.siteCompanyId) {
          form.setFieldValue("siteCompany", well.siteCompanyId);
        }
      }

      const selectedPickupSiteId = form.getTransformedValues().site;

      if (well.siteId) {
        if (selectedPickupSiteId !== well.siteId) {
          form.setFieldValue("site", well.siteId);
        }
      }
    }
  }, [form]);

  const handleContainerChange = useCallback((container: ContainerEntity | undefined): void => {
    if (container) {
      const selectedPickupSiteCompanyId = form.getTransformedValues().siteCompany;

      if (container.siteCompanyId) {
        if (selectedPickupSiteCompanyId !== container.siteCompanyId) {
          form.setFieldValue("siteCompany", container.siteCompanyId);
        }
      }

      const selectedPickupSiteId = form.getTransformedValues().site;

      if (container.siteId) {
        if (selectedPickupSiteId !== container.siteId) {
          form.setFieldValue("site", container.siteId);
        }
      }
    }
  }, [form]);

  const onCategoryChange = useCallback((value: string) => {
    const newCategory = toTransportCategory(value)

    setCategory(newCategory)

    form.setFieldValue("category", value)

    if (newCategory === transportCategory.oil) {
      form.setFieldValue("serviceType", undefined)
    }
  }, [form])

  const onGaugeFormClose = useCallback(() => {
    setSelectedGauge(undefined)
    gaugeHandler.close()
  }, [])

  const onGaugeFormSubmit = useCallback((values: TransportGaugeFormValues) => {
    let gaugeIndex: number | undefined = undefined

    const key = values.type?.code + "~" + (getObjectId(values.container) ?? "null")

    const gauges = form.values.gauges

    gauges.forEach((gauge, index) => {
      const gaugeKey = gauge.type?.code + "~" + (getObjectId(gauge.container) ?? "null")
      if (gaugeKey === key) {
        gaugeIndex = index;
      }
    });

    if (typeof gaugeIndex !== 'undefined') {
      form.setFieldValue(`gauges.${gaugeIndex}.container`, values.container)
      form.setFieldValue(`gauges.${gaugeIndex}.type`, values.type)
      form.setFieldValue(`gauges.${gaugeIndex}.topValue`, values.topValue)
      form.setFieldValue(`gauges.${gaugeIndex}.bottomValue`, values.bottomValue)
    } else {
      form.insertListItem('gauges', values)
    }

    gaugeHandler.close()
  }, [form, gaugeHandler])

  const onGaugeEditClick = useCallback((values: TransportGaugeFormValues) => {
    setSelectedGauge(values)
    gaugeHandler.open()
  }, [gaugeHandler])

  const onGaugeDeleteClick = useCallback((values: TransportGaugeFormValues, index: number) => {
    form.removeListItem('gauges', index)
  }, [form])

  return (
    <form ref={ref} onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconCalendar/>
        </Grid.Col>
        <Grid.Col span={12}>
          <DateInput
            size="xs"
            valueFormat="MM/DD/YYYY HH:mm:ss"
            label={t("common.entity.transport.form.field.date", "Date")}
            placeholder={t<string>("common.entity.transport.form.field.date.placeholder", "Date")}
            styles={{wrapper: {width: "50%"}}}
            withAsterisk
            required
            {...form.getInputProps("date")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <DateInput
            size="xs"
            valueFormat="MM/DD/YYYY HH:mm:ss"
            label={t("common.entity.transport.form.field.indate", "In Date")}
            placeholder={t<string>("common.entity.transport.form.field.indate.placeholder", "In Date")}
            disabled={!form.getTransformedValues().skipGeofenceEvent && options?.hasGeofenceEvent}
            {...form.getInputProps("inDate")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <DateInput
            size="xs"
            valueFormat="MM/DD/YYYY HH:mm:ss"
            label={t("common.entity.transport.form.field.outdate", "Out Date")}
            placeholder={t<string>("common.entity.transport.form.field.outdate.placeholder", "Out Date")}
            disabled={!form.getTransformedValues().skipGeofenceEvent && options?.hasGeofenceEvent}
            {...form.getInputProps("outDate")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <Checkbox
            size="xs"
            label={t("common.entity.transport.form.field.disabledemurrage", "Demurrage Disabled")}
            {...form.getInputProps("disableDemurrage", {type: "checkbox"})}
            mb="sm"
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Checkbox
            size="xs"
            label={t("common.entity.transport.form.field.skipgeofenceevent", "Skip Geofence Event")}
            {...form.getInputProps("skipGeofenceEvent", {type: "checkbox"})}
            mb="sm"
          />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconUser/>
        </Grid.Col>
        <Grid.Col span={4}>
          <DriverSelect
            size="xs"
            disabled={options?.personSelectionDisabled}
            label={t("common.entity.transport.form.field.person", "Driver")}
            placeholder={t<string>("common.entity.transport.form.field.person.placeholder", "Driver")}
            selector={{
              regionId: options?.regionId
            }}
            clearable
            withAsterisk
            required
            {...form.getInputProps("person")} />
          <Group grow>
            <NumberInput
              size="xs"
              label={t("common.entity.transport.form.field.odometer", "Odometer")}
              placeholder={t<string>("common.entity.transport.form.field.odometer.placeholder", "Odometer")}
              precision={5}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              {...form.getInputProps("odometer")} />
            <UnitOfMeasureSelect
              size="xs"
              label={t("common.entity.transport.form.field.uom", "Units")}
              placeholder={t<string>("common.entity.transport.form.field.uom.placeholder", "Units")}
              selector={{
                types: [unitOfMeasureTypes.linear]
              }}
              withAsterisk={typeof form.getTransformedValues().odometer !== 'undefined'}
              required={typeof form.getTransformedValues().odometer !== 'undefined'}
              clearable
              {...form.getInputProps("odometerUom")} />
          </Group>
        </Grid.Col>
        <Grid.Col span={4}>
          <VehicleSelect
            size="xs"
            label={t("common.entity.transport.form.field.vehicle", "Truck")}
            placeholder={t<string>("common.entity.transport.form.field.vehicle.placeholder", "Truck")}
            selector={{
              regionId: options?.regionId
            }}
            clearable
            {...form.getInputProps("vehicle")} />
          <TrailerSelect
            size="xs"
            label={t("common.entity.transport.form.field.trailer", 'Trailer')}
            placeholder={t<string>("common.entity.transport.form.field.trailer.placeholder", "Trailer")}
            selector={{
              regionId: options?.regionId
            }}
            clearable
            withAsterisk
            required
            {...form.getInputProps("trailer")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconStack/>
        </Grid.Col>
        <Grid.Col span={12}>
          <SegmentedControl
            size="xs"
            my={"md"}
            value={category.code}
            onChange={onCategoryChange}
            color={getTransportCategoryColorRefByType(category)}
            data={transportCategories.map((c) => {
              return {
                label: t<string>(c.displayValue.id, c.displayValue.defaultMessage),
                value: c.code,
                disabled: options?.categoryDisabled,
              }
            })}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <FreightTypeSelect
            size="xs"
            label={t("common.entity.transport.form.field.freighttype", "Freight Type")}
            placeholder={t<string>("common.entity.transport.form.field.freighttype.placeholder", "Freight Type")}
            selector={{
              regionId: options?.regionId,
              cargoSubtype: category === transportCategory.oil ? cargoSubtype.oil : undefined
            }}
            clearable
            withAsterisk
            required
            {...form.getInputProps("freightType")} />
        </Grid.Col>
        <Grid.Col span={4}>
          {category === transportCategory.water &&
            <ServiceTypeSelect
              size="xs"
              label={t("common.entity.transport.form.field.servicetype", "Service Type")}
              placeholder={t<string>("common.entity.transport.form.field.servicetype.placeholder", "Service Type")}
              selector={{
                regionId: options?.regionId,
              }}
              clearable
              {...form.getInputProps("serviceType")} />
          }
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={8}>
          <Group grow>
            <NumberInput
              size="xs"
              label={t("common.entity.transport.form.field.quantity", "Quantity")}
              placeholder={t<string>("common.entity.transport.form.field.quantity.placeholder", "Quantity")}
              precision={5}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              withAsterisk
              required
              {...form.getInputProps("quantity")} />
            <UnitOfMeasureSelect
              size="xs"
              label={t("common.entity.transport.form.field.uom", "Units")}
              placeholder={t<string>("common.entity.transport.form.field.uom.placeholder", "Units")}
              selector={{
                types: [unitOfMeasureTypes.volume, unitOfMeasureTypes.weight, unitOfMeasureTypes.volumeSolid]
              }}
              clearable
              withAsterisk
              required
              {...form.getInputProps("quantityUom")} />
          </Group>
        </Grid.Col>
      </Grid>
      {category === transportCategory.oil &&
        <>
          <Grid grow gutter="xs">
            <Grid.Col span={4}>
              <NumberInput
                size="xs"
                label={t("common.entity.transport.form.field.obsgravity", "Obs. Gravity")}
                placeholder={t<string>("common.entity.transport.form.field.obsgravity.placeholder", "Obs. Gravity")}
                precision={5}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                withAsterisk={type === transportType.pickup}
                required={type === transportType.pickup}
                {...form.getInputProps("observedGravity")} />
            </Grid.Col>
            <Grid.Col span={4}/>
          </Grid>
          <Grid grow gutter="xs">
            <Grid.Col span={4}>
              <NumberInput
                size="xs"
                label={t("common.entity.transport.form.field.bswpercentage", "BS&W %")}
                placeholder={t<string>("common.entity.transport.form.field.bswpercentage.placeholder", "BS&W %")}
                precision={5}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                withAsterisk={type === transportType.pickup}
                required={type === transportType.pickup}
                {...form.getInputProps("bswPercentage")} />
            </Grid.Col>
            <Grid.Col span={4}/>
          </Grid>
          <Divider my="sm" variant="dotted"/>
          <Grid grow gutter="xs">
            <Grid.Col span={4}>
              <NumberInput
                size="xs"
                label={t("common.entity.transport.form.field.obstemperature", "Obs. Temp.")}
                placeholder={t<string>("common.entity.transport.form.field.obstemperature.placeholder", "Obs. Temp")}
                precision={5}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                withAsterisk={type === transportType.pickup}
                required={type === transportType.pickup}
                {...form.getInputProps("observedTemperature")} />
            </Grid.Col>
            <Grid.Col span={4}>
              <UnitOfMeasureSelect
                size="xs"
                label={t("common.entity.transport.form.field.uom", "Units")}
                placeholder={t<string>("common.entity.transport.form.field.uom.placeholder", "Units")}
                selector={{
                  types: [unitOfMeasureTypes.temperature]
                }}
                clearable
                withAsterisk={type === transportType.pickup}
                required={type === transportType.pickup}
                {...form.getInputProps("temperatureUom")} />
            </Grid.Col>
          </Grid>
          <Grid grow gutter="xs">
            <Grid.Col span={4}>
              <NumberInput
                size="xs"
                label={t("common.entity.transport.form.field.toptemperature", "Top Temp.")}
                placeholder={t<string>("common.entity.transport.form.field.toptemperature.placeholder", "Top Temp")}
                precision={5}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                {...form.getInputProps("topTemperature")} />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                size="xs"
                label={t("common.entity.transport.form.field.bottomtemperature", "Bottom Temp.")}
                placeholder={t<string>("common.entity.transport.form.field.bottomtemperature.placeholder", "Bottom Temp")}
                precision={5}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                {...form.getInputProps("bottomTemperature")} />
            </Grid.Col>
          </Grid>
          <Divider my="sm" variant="dotted"/>
          <Grid grow gutter="xs">
            <Grid.Col span={4}>
              <NumberInput
                size="xs"
                label={t("common.entity.transport.form.field.correctedgravity", "Corrected Gravity")}
                placeholder={t<string>("common.entity.transport.form.field.correctedgravity.placeholder", "Corrected Gravity")}
                precision={5}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                withAsterisk={type === transportType.pickup}
                required={type === transportType.pickup}
                {...form.getInputProps("correctedGravity")} />
            </Grid.Col>
            <Grid.Col span={4}/>
          </Grid>
          <Grid grow gutter="xs">
            <Grid.Col span={4}>
              <NumberInput
                size="xs"
                label={t("common.entity.transport.form.field.netquantity", "Net Quantity")}
                placeholder={t<string>("common.entity.transport.form.field.netquantity.placeholder", "Net Quantity")}
                precision={5}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                withAsterisk={type === transportType.pickup}
                required={type === transportType.pickup}
                {...form.getInputProps("netQuantity")} />
            </Grid.Col>
            <Grid.Col span={4}/>
          </Grid>
          <Divider my="sm" variant="dotted"/>
          <Grid grow gutter="xs">
            <Grid.Col span={4}>
              <TransportRejectionTypeSelect
                size="xs"
                label={t("common.entity.transport.form.field.rejection", "Rejection")}
                placeholder={t<string>("common.entity.transport.form.field.rejection.placeholder", "Rejection")}
                clearable
                {...form.getInputProps("rejectionType")} />
            </Grid.Col>
            <Grid.Col span={4}>
            </Grid.Col>
          </Grid>
          <Grid grow gutter="xs">
            <Grid.Col span={8}>
              <Textarea
                size="xs"
                label={t("common.entity.transport.form.field.rejectioncomments", "Rejection Comments")}
                placeholder={t<string>("common.entity.transport.form.field.rejectioncomments.placeholder", "Rejection Comments")}
                {...form.getInputProps("rejectionComment")}
                withAsterisk={!isStringUndefinedOrBlank(form.getTransformedValues().rejectionType)}
                required={!isStringUndefinedOrBlank(form.getTransformedValues().rejectionType)}/>
            </Grid.Col>
          </Grid>
        </>
      }
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconLocation/>
        </Grid.Col>
        <Grid.Col span={6}>
          <CompanySelect
            size="xs"
            disabled={options?.siteSelectionDisabled}
            label={t("common.entity.transport.form.field.sitecompany", "Site Company")}
            placeholder={t<string>("common.entity.transport.form.field.sitecompany.placeholder", "Site Company")}
            selector={{
              regionId: options?.regionId,
            }}
            onEntityChange={(e) => handleSiteCompanyChange(firstOrUndefined(e))}
            clearable
            {...form.getInputProps("siteCompany")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <SiteSelect
            size="xs"
            disabled={options?.siteSelectionDisabled}
            label={t("common.entity.transport.form.field.site", "Site")}
            placeholder={t<string>("common.entity.transport.form.field.site.placeholder", "Site")}
            selector={{
              regionId: options?.regionId,
              company: form.getTransformedValues().siteCompany
            }}
            onEntityChange={(e) => handleSiteChange(firstOrUndefined(e))}
            clearable
            withAsterisk
            required
            {...form.getInputProps("site")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <WellSelect
            size="xs"
            disabled={options?.wellSelectionDisabled}
            label={t("common.entity.transport.form.field.well", "Well")}
            placeholder={t<string>("common.entity.transport.form.field.well.placeholder", "Well")}
            selector={{
              regionId: options?.regionId,
              siteId: form.getTransformedValues().site
            }}
            onEntityChange={(e) => handleWellChange(firstOrUndefined(e))}
            clearable
            {...form.getInputProps("well")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <ContainerSelect
            size="xs"
            disabled={options?.containerSelectionDisabled}
            label={t("common.entity.transport.form.field.container", "Container")}
            placeholder={t<string>("common.entity.transport.form.field.container.placeholder", "Container")}
            useAbbreviation={typeof form.getTransformedValues().site !== "undefined"}
            selector={{
              regionId: options?.regionId,
              siteId: form.getTransformedValues().site ?? "-1",
              cargoSubtype: category === transportCategory.oil ? cargoSubtype.oil : undefined,
            }}
            onEntityChange={(e) => handleContainerChange(firstOrUndefined(e))}
            clearable
            {...form.getInputProps("container")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconRulerMeasure/>
        </Grid.Col>
        {form.values.gauges.length > 0 &&
          <Grid.Col span={12}>
            <Card shadow="sm" padding="sm" mb="sm" radius="sm">
              <Table>
                <thead>
                <tr>
                  <th>
                    Container
                  </th>
                  <th>
                    Type
                  </th>
                  <th>
                    Top
                  </th>
                  <th>
                    Bottom
                  </th>
                  <th/>
                </tr>
                </thead>
                <tbody>
                {form.values.gauges.map((g, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        {getObjectName(g.container) ?? "Ticket Container"}
                      </td>
                      <td>
                        {t(g.type.displayValue.id, g.type.displayValue.defaultMessage)}
                      </td>
                      <td>
                        {FeetInches.fromInches(g.topValue).toString()}
                      </td>
                      <td>
                        {FeetInches.fromInches(g.bottomValue).toString()}
                      </td>
                      <td>
                        <Group>
                          <ActionIcon onClick={() => onGaugeEditClick(g)}>
                            <IconEdit size={16}/>
                          </ActionIcon>
                          <HasAccess permissions={["edit"]}>
                            <ActionIcon onClick={() => onGaugeDeleteClick(g, index)}>
                              <IconTrash color="red" size={16}/>
                            </ActionIcon>
                          </HasAccess>
                        </Group>
                      </td>
                    </tr>
                  )
                })}
                </tbody>
              </Table>
            </Card>
          </Grid.Col>
        }
        <Grid.Col span={12}>
          <Button onClick={() => gaugeHandler.open()}>
            Add Gauge
          </Button>
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconGauge/>
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            size="xs"
            label={t("common.entity.transport.form.field.meterstart", "Meter Start")}
            placeholder={t<string>("common.entity.transport.form.field.quantity.meterstart", "Meter Start")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            {...form.getInputProps("meterStart")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            size="xs"
            label={t("common.entity.transport.form.field.meterend", "Meter End")}
            placeholder={t<string>("common.entity.transport.form.field.quantity.meterend", "Meter End")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            {...form.getInputProps("meterEnd")} />
        </Grid.Col>
        <Grid.Col span={6}/>
        <Grid.Col span={6}>
          <UnitOfMeasureSelect
            size="xs"
            label={t("common.entity.transport.form.field.uom", "Units")}
            placeholder={t<string>("common.entity.transport.form.field.uom.placeholder", "Units")}
            clearable
            {...form.getInputProps("meterUom")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconTag/>
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.sealoff", "Seal Off")}
            placeholder={t<string>("common.entity.transport.form.field.sealoff", "Seal Off")}
            {...form.getInputProps("sealOff")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.sealon", "Seal On")}
            placeholder={t<string>("common.entity.transport.form.field.sealon", "Seal On")}
            {...form.getInputProps("sealOn")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <ValveTypeSelect
            size="xs"
            label={t("common.entity.transport.form.field.sealvalvetype", "Seal Valve Type")}
            placeholder={t<string>("common.entity.transport.form.field.sealvalvetype", "Seal Valve Type")}
            clearable
            {...form.getInputProps("sealValveType")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.seal2off", "Seal 2 Off")}
            placeholder={t<string>("common.entity.transport.form.field.seal2off", "Seal 2 Off")}
            {...form.getInputProps("seal2Off")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.seal2on", "Seal 2 On")}
            placeholder={t<string>("common.entity.transport.form.field.seal2on", "Seal 2 On")}
            {...form.getInputProps("seal2On")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <ValveTypeSelect
            size="xs"
            label={t("common.entity.transport.form.field.seal2valvetype", "Seal 2 Valve Type")}
            placeholder={t<string>("common.entity.transport.form.field.seal2valvetype", "Seal 2 Valve Type")}
            clearable
            {...form.getInputProps("seal2ValveType")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.seal3off", "Seal 3 Off")}
            placeholder={t<string>("common.entity.transport.form.field.seal3off", "Seal 3 Off")}
            {...form.getInputProps("seal3Off")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.seal3on", "Seal 3 On")}
            placeholder={t<string>("common.entity.transport.form.field.seal3on", "Seal 3 On")}
            {...form.getInputProps("seal3On")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <ValveTypeSelect
            size="xs"
            label={t("common.entity.transport.form.field.seal3valvetype", "Seal 3 Valve Type")}
            placeholder={t<string>("common.entity.transport.form.field.seal3valvetype", "Seal 3 Valve Type")}
            clearable
            {...form.getInputProps("seal3ValveType")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconDots/>
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.ticketnumber", "Ticket #")}
            placeholder={t<string>("common.entity.transport.form.field.ticketnumber.placeholder", "Ticket #")}
            {...form.getInputProps("ticketNumber")} />
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.internalworkordernumber", "Internal W.O. #")}
            placeholder={t<string>("common.entity.transport.form.field.internalworkordernumber.placeholder", "Internal W.O. #")}
            {...form.getInputProps("internalWorkOrderNumber")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.transport.form.field.workordernumber", "W.O. #")}
            placeholder={t<string>("common.entity.transport.form.field.workordernumber.placeholder", "W.O. #")}
            {...form.getInputProps("workOrderNumber")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <IconMessage/>
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            size="xs"
            label={t("common.entity.transport.form.field.comments", "Comments")}
            placeholder={t<string>("common.entity.transport.form.field.comments.placeholder", "Comment")}
            {...form.getInputProps("comments")} />
        </Grid.Col>
      </Grid>
      {onSubmit &&
        <Button type="submit" loading={submitting} fullWidth mt="xl">
          {t("common.action.submit", {ns: "common"})}
        </Button>
      }

      <TransportGaugeFormModal
        title={t<string>("common.action.filter", "Filter", {ns: "common"})}
        category={category}
        opened={gaugeOpened}
        onClose={onGaugeFormClose}
        onSubmit={onGaugeFormSubmit}
        defaultValues={{
          type: selectedGauge?.type,
          container: selectedGauge?.container,
          topValue: selectedGauge?.topValue,
          bottomValue: selectedGauge?.bottomValue
        }}
      />

    </form>
  )
});
